import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { observer } from 'mobx-react';
import { Button } from '@mui/material';
import { styled } from '@mui/system';
import { isEmpty } from 'lodash';
import { init } from 'echarts';
import { round } from 'mathjs';

import Color from '@tool/color';

interface IProps {
  height: number,
  xData: any[],
  barData: any[],
  lineData: any[],
}
interface IState {}

const TimeChart = (props: IProps, state: IState) => {
  const navi = useNavigate();
  let chart: any = null;
  const { height, xData, barData, lineData } = props;
  const timeChart = useRef<any>(null);

  // 返回number数值 第二个参数 - 保留几位小数
  const calcVal = (value: any, digit: number = 0) => {
    // eslint-disable-next-line no-restricted-globals
    if (isNaN(value)) return '-';
    if (value === null) return '-';
    return round(value, digit);
  };

  // 第二个参数 - 保留几位小数
  const newRate = (value: any, digit: number = 1) => {
    // eslint-disable-next-line no-restricted-globals
    if (isNaN(value)) return '-';
    if (value === null || value === '' || value === undefined) return '-';
    return `${round(value * 100, digit)}%`;
  };

  const render = () => {
    if (chart) chart.dispose();
    chart = init(timeChart.current);

    const option = {
      color: [Color.primary, Color.secondary],
      tooltip: {
        show: false,
        trigger: 'axis',
        axisPointer: {
          type: 'shadow',
          shadowStyle: {
            color: Color.primaryColor(0.04),
          },
          label: { show: true },
        },
        textStyle: {
          color: Color.blackColor(0.85),
        },
        formatter: (params: any) => {
          return `
            <div class="tip-0555">
              <b>${params[0].axisValueLabel}</b>
              <p class="up-022">
                <span class="span01">
                  <em class="em-01 em-011"></em>
                  <strong>${params[0]?.seriesName || '-'}</strong>
                </span>
                <span class="span02">${params[0]?.value}人</span>
              </p>
              <p class="up-022">
                <span class="span01">
                  <em class="em-03"></em>
                  <strong>${params[1]?.seriesName || '-'}</strong>
                </span>
                <span class="span02">${round(params[1].value * 100, 1)}%</span>
              </p>
            </div>
          `;
        },
      },
      grid: {
        left: 0,
        right: 20,
        bottom: 40,
        top: 20,
        containLabel: true,
      },
      legend: {
        bottom: 0,
        itemWidth: 12,
        itemHeight: 8,
        itemGap: 20,
        padding: 0,
        data: [
          {
            name: '人员规模',
            itemStyle: {
              color: Color.primary,
            },
            lineStyle: {
              color: Color.primary,
            },
          },
          {
            name: '变化率',
            itemStyle: {
              color: Color.secondary,
            },
            lineStyle: {
              width: 1,
              color: Color.secondary,
            },
          },
        ],
      },
      xAxis: [
        {
          type: 'category',
          data: xData,
          axisTick: {
            show: false,
          },
          axisLine: {
            lineStyle: {
              color: Color.ccc,
            },
          },
          axisLabel: {
            color: Color.blackColor(1),
          },
        },
      ],
      yAxis: [
        {
          type: 'value',
          splitLine: {
            show: true,
            lineStyle: {
              color: Color.blackColor(0.008),
              width: 1,
              type: 'dashed',
            },
          },
          axisLabel: {
            color: Color.da,
            fontFamily: 'Roboto',
          },
        },
        {
          type: 'value',
          // min: 0,
          // max: 1,
          gridIndex: 0,
          axisLine: { show: false },
          axisTick: { show: false },
          axisLabel: {
            show: false,
            formatter: (val: number) => {
              if (val === 0) return 0;
              return `${round(val * 100, 1)}%`;
            },
            fontFamily: 'Roboto',
            color: Color.blackColor(0.5),
          },
          splitLine: {
            show: true,
            lineStyle: {
              color: Color.blackColor(0.08),
              width: 1,
              type: 'dashed',
            },
          },
        },
      ],
      animation: false,
      series: [
        {
          name: '人员规模',
          type: 'bar',
          barWidth: '20px',
          data: barData,
          yAxisIndex: 0,
          itemStyle: {
            borderRadius: [2, 2, 0, 0],
          },
          markLine: {},
        },
        {
          name: '变化率',
          data: lineData,
          type: 'line',
          symbol: 'circle',
          symbolSize: 7,
          yAxisIndex: 1,
          label: {
            show: true,
            fontFamily: 'Roboto',
            color: Color.secondary,
            formatter: (item: any) => {
              return `${round(item.value * 100, 1)}%`;
            },
          },
        },
      ],
    };
    chart.setOption(option, true);
  };

  useEffect(() => {
    render();
    window.addEventListener('resize', render);

    return () => {
      window.removeEventListener('resize', render);
    };
  }, [xData, barData, lineData]);

  return (
    <section ref={timeChart}
      style={{ width: '100%', height: `${height}px` }} />
  );
};

export default observer(TimeChart);
